/*
Similar to the upload form that it controls the attachments assigned to a mail message
However, this will display it in a more outlook-esque view, within a grid/flex layout.

The user can still add and remove attachments with this form too but the upload form just displays
a single dropzone element as this one shows an upload button AND also all current attachments.
*/

import React from "react";
import styled from "styled-components";
import { useAutoAnimate } from "@formkit/auto-animate/react"
import { FieldArrayWithId } from "react-hook-form";
import { DocumentDetailsCard } from "../../utilities/DocumentDetailsCard";
import { MailMessageUploadFormProps } from "./MailMessageAttachmentsUploadForm";

const MailMessageAttachmentGrid = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;

    & > * {
        flex: 1 1 25%;
    }
`;

export type MailMessageAttachmentsDisplayFormProps = MailMessageUploadFormProps & { enableFileProtection: boolean };
type MailMessageAttachmentRecordProps = FieldArrayWithId<MailMessage, 'attachments', 'id'> & {
    index: number,
    clearFileCallback: () => void
    enableFileProtection: boolean
    fileProtectionChangedCallback: () => void
};

const MailMessageAttachmentRecord = ({ 
    clearFileCallback, 
    enableFileProtection,
    fileType, 
    fileProtectionChangedCallback, 
    name, 
    size, 
    password = null, 
    __isLocked__ = false 
} : MailMessageAttachmentRecordProps) => {
    return <DocumentDetailsCard 
        fileName={name} 
        fileSize={size} 
        type={fileType}
        isClearable={__isLocked__ === true ? false : true}
        isProtected={enableFileProtection === true ? password !== null ? true : false : null}
        protectedFileIconClick={fileProtectionChangedCallback}
        clearFile={clearFileCallback}
        clearIcon={['fas', 'trash-alt']}
        clearIconSize="sm"
        clearTooltipText="Remove attachment."
        clearTooltipTextAlt="Unable to remove attachment as it is a required file."
    />
};

export const MailMessageAttachmentsDisplayForm = ({ enableFileProtection, fields, remove, update } : MailMessageAttachmentsDisplayFormProps) => {
    const [parent] = useAutoAnimate();

    const onFileCleared = (index: number) : void => remove(index);

    const onFileProtectionChanged = (index: number, el:MailMessageAttachment) : void => {
        if (el.password && el.password.length > 0) {
            update(index, { ...el, password: null });
        }
        else {
            update(index, { ...el, password: "{{__DEFAULT__}}" });
        }
    }

    if (!fields || fields.length === 0) {
        return null;
    }

    return (
        <MailMessageAttachmentGrid ref={parent}>
            {fields.map((el, i) => (
                <MailMessageAttachmentRecord 
                    key={el.id} 
                    index={i} 
                    clearFileCallback={() => onFileCleared(i)}
                    enableFileProtection={enableFileProtection}
                    fileProtectionChangedCallback={() => onFileProtectionChanged(i, el)} 
                    {...el}
                />
            ))}
        </MailMessageAttachmentGrid>
    );
};